import {
  ApplCheckCreateFormStep1,
  ApplCheckCreateFormStep2,
  ApplCheckLawConclusionPage,
  ApplCheckUnderConclusionPage,
  ApplicationCheckDetailsPage,
  ApplicationCheckPage,
  LawyerConclusionPreview,
} from '@/pages/application-check';
import UnderwriterConclusionPreview from '@/pages/application-check/ui/UnderwriterConclusionPreview/UnderwriterConclusionPreview';

import {
  BankBranchesDetailsPage,
  BankBranchesPage,
} from '@/pages/bank-branches';
import {
  CreditApplicationFormPage,
  CreditApplicationsDetailsPage,
  CreditApplicationSmartFormPage,
  CreditApplicationsPage,
} from '@/pages/credit-applications';
import {
  CreditProductDetailsPage,
  CreditProductPage,
} from '@/pages/credit-product';
import { DocumentsDetailsPage, DocumentsPage } from '@/pages/documents';
import { EmployeesDetailsPage, EmployeesPage } from '@/pages/employees';
import {
  FarmerCriteriaDetailsPage,
  FarmerCriteriaFormPage,
  FarmerCriteriaIndexPage,
} from '@/pages/farmer-criteria';
import { LogoutPage } from '@/pages/logout';
import {
  MonitoringCreatePage,
  MonitoringDetailsPage,
  MonitoringListPage,
  MonitoringUpdatePage,
} from '@/pages/monitoring';
import { PERMISSIONS } from '@/shared/const/permissions.const';
import {
  AppRoutes,
  getRouteApplCheckCreateStep1,
  getRouteApplCheckCreateStep2,
  getRouteApplicationCheck,
  getRouteApplicationCheckDetails,
  getRouteBankBranches,
  getRouteBankBranchesDetails,
  getRouteConclusionForm,
  getRouteCreditApplications,
  getRouteCreditApplicationsCreate,
  getRouteCreditApplicationsCreateSmart,
  getRouteCreditApplicationsDetails,
  getRouteCreditProducts,
  getRouteCreditProductsDetails,
  getRouteDocuments,
  getRouteDocumentsDetails,
  getRouteEmployees,
  getRouteEmployeesDetails,
  getRouteFarmerCriteria,
  getRouteFarmerCriteriaDetails,
  getRouteFarmerCriteriaForm,
  getRouteLawyerConclusionPreview,
  getRouteLogout,
  getRouteMonitoringCreate,
  getRouteMonitoringDetails,
  getRouteMonitoringList,
  getRouteMonitoringUpdate,
  getRouteUnderwriterConclusionForm,
  getRouteUnderwriterConclusionPreview,
} from '@/shared/const/router.const.ts';
import { RouteProps } from 'react-router';

export type AppRoutesProps = RouteProps & {
  authOnly?: boolean;
  permissions?: string[];
};

export const routes: Record<AppRoutes, AppRoutesProps> = {
  [AppRoutes.LOGOUT]: {
    path: getRouteLogout(),
    element: <LogoutPage />,
    authOnly: true,
  },
  [AppRoutes.EMPLOYEES]: {
    path: getRouteEmployees(),
    element: <EmployeesPage />,
    authOnly: true,
    permissions: [PERMISSIONS.USERS.INDEX],
  },
  [AppRoutes.EMPLOYEES_DETAILS]: {
    path: getRouteEmployeesDetails(':id'),
    element: <EmployeesDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.USERS.VIEW],
  },
  [AppRoutes.CREDIT_APPLICATIONS]: {
    path: getRouteCreditApplications(),
    element: <CreditApplicationsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.LOANS.INDEX],
  },
  [AppRoutes.CREDIT_APPLICATIONS_DETAILS]: {
    path: getRouteCreditApplicationsDetails(':id'),
    element: <CreditApplicationsDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.LOANS.VIEW],
  },
  [AppRoutes.CREDIT_APPLICATIONS_CREATE]: {
    path: getRouteCreditApplicationsCreate(':loanId?'),
    element: <CreditApplicationFormPage />,
    authOnly: true,
    permissions: [PERMISSIONS.LOANS.CREATE, PERMISSIONS.LOANS.UPDATE],
  },
  [AppRoutes.CREDIT_APPLICATIONS_CREATE_SMART]: {
    path: getRouteCreditApplicationsCreateSmart(':loanId?'),
    element: <CreditApplicationSmartFormPage />,
    authOnly: true,
    permissions: [PERMISSIONS.LOANS.CREATE, PERMISSIONS.LOANS.UPDATE],
  },
  [AppRoutes.DOCUMENTS]: {
    path: getRouteDocuments(),
    element: <DocumentsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.DOCUMENTS.INDEX],
  },
  [AppRoutes.DOCUMENTS_DETAILS]: {
    path: getRouteDocumentsDetails(':uuid'),
    element: <DocumentsDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.DOCUMENTS.VIEW],
  },
  [AppRoutes.APPLICATION_CHECK]: {
    path: getRouteApplicationCheck(),
    element: <ApplicationCheckPage />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.INDEX],
  },
  [AppRoutes.APPLICATION_CHECK_DETAILS]: {
    path: getRouteApplicationCheckDetails(':id'),
    element: <ApplicationCheckDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.VIEW],
  },
  [AppRoutes.PRODUCTS]: {
    path: getRouteCreditProducts(),
    element: <CreditProductPage />,
    authOnly: true,
    permissions: [PERMISSIONS.PRODUCTS.INDEX],
  },
  [AppRoutes.CREDIT_PRODUCTS_DETAILS]: {
    path: getRouteCreditProductsDetails(':id'),
    element: <CreditProductDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.PRODUCTS.VIEW],
  },
  [AppRoutes.BANK_BRANCHES]: {
    path: getRouteBankBranches(),
    element: <BankBranchesPage />,
    authOnly: true,
    permissions: [PERMISSIONS.PARTNERS.INDEX],
  },
  [AppRoutes.BANK_BRANCHES_DETAILS]: {
    path: getRouteBankBranchesDetails(':id'),
    element: <BankBranchesDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.PARTNERS.VIEW],
  },
  [AppRoutes.MONITORING_LIST]: {
    path: getRouteMonitoringList(),
    element: <MonitoringListPage />,
    authOnly: true,
    permissions: [PERMISSIONS.MONITORING.INDEX],
  },
  [AppRoutes.MONITORING_DETAILS]: {
    path: getRouteMonitoringDetails(':id'),
    element: <MonitoringDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.MONITORING.VIEW],
  },
  [AppRoutes.MONITORING_CREATE]: {
    path: getRouteMonitoringCreate(':id?'),
    element: <MonitoringCreatePage />,
    authOnly: true,
    permissions: [PERMISSIONS.MONITORING.CREATE],
  },
  [AppRoutes.MONITORING_UPDATE]: {
    path: getRouteMonitoringUpdate(':id'),
    element: <MonitoringUpdatePage />,
    authOnly: true,
    permissions: [PERMISSIONS.MONITORING.UPDATE],
  },
  [AppRoutes.APPLICATION_CHECK_CREATE_STEP1]: {
    path: getRouteApplCheckCreateStep1(),
    element: <ApplCheckCreateFormStep1 />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.CREATE],
  },
  [AppRoutes.APPLICATION_CHECK_CREATE_STEP2]: {
    path: getRouteApplCheckCreateStep2(':id'),
    element: <ApplCheckCreateFormStep2 />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.CREATE],
  },
  [AppRoutes.LAWYER_CONCLUSION]: {
    path: getRouteConclusionForm(':id'),
    element: <ApplCheckLawConclusionPage />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.LAWYER_CONCLUSION],
  },
  [AppRoutes.UNDERWRITER_CONCLUSION]: {
    path: getRouteUnderwriterConclusionForm(':id'),
    element: <ApplCheckUnderConclusionPage />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.UNDERWRITER_CONCLUSION],
  },
  [AppRoutes.LAWYER_CONCLUSION_PREVIEW]: {
    path: getRouteLawyerConclusionPreview(':id'),
    element: <LawyerConclusionPreview />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.LAWYER_CONCLUSION],
  },
  [AppRoutes.UNDERWRITER_CONCLUSION_PREVIEW]: {
    path: getRouteUnderwriterConclusionPreview(':id'),
    element: <UnderwriterConclusionPreview />,
    authOnly: true,
    permissions: [PERMISSIONS.APPLICATIONS.UNDERWRITER_CONCLUSION],
  },
  [AppRoutes.FARMER_CRITERIA]: {
    path: getRouteFarmerCriteria(),
    element: <FarmerCriteriaIndexPage />,
    authOnly: true,
    permissions: [PERMISSIONS.FARMER_CRITERIA.INDEX],
  },
  [AppRoutes.FARMER_CRITERIA_FORM]: {
    path: getRouteFarmerCriteriaForm(':id?'),
    element: <FarmerCriteriaFormPage />,
    authOnly: true,
    permissions: [
      PERMISSIONS.FARMER_CRITERIA.CREATE,
      PERMISSIONS.FARMER_CRITERIA.UPDATE,
    ],
  },
  [AppRoutes.FARMER_CRITERIA_DETAILS]: {
    path: getRouteFarmerCriteriaDetails(':id'),
    element: <FarmerCriteriaDetailsPage />,
    authOnly: true,
    permissions: [PERMISSIONS.FARMER_CRITERIA.VIEW],
  },
};
